// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { useQuery } from "@apollo/client";
import {
  mdiContentSavePlus,
  mdiDelete,
  mdiDotsVertical,
  mdiFormTextbox,
  mdiPencil
} from "@mdi/js";
import { Popconfirm, Popover } from "antd";
import {
  ForecastActivityType,
  TYPE_WELLS,
  USER_ARPS
} from "constants/settings.constants";
import { ICheckedForecast } from "store/features";
import { RootState } from "store/rootReducer";
import {
  selectCheckedForecasts,
  selectCheckedTypeWells,
  selectHasUnsavedChanges,
  selectTemporaryForecast,
  useForecastStore
} from "stores/forecast";
import styled from "styled-components";

import { useUser } from "hooks";

import { WORKSPACES, WorkspaceClientName } from "api/workspace";

import { UserArpsItem } from "models/UserArpsModel";
import { WorkspaceQuery } from "models/workspace";

import { UnsavedTypeWellChangesPopconfirm } from "components/arps/UnsavedTypeWellChangesPopconfirm";
import { useForecastFolderUpdater } from "components/arps/hooks";
import { Tooltip } from "components/base";
import { TypeWellEditorWidgetKey } from "components/dashboard/constants/widgets.constants";
import { useDashboardContext } from "components/dashboard/hooks";
import { DefaultTypeWellDashboard } from "components/user-settings/models";
import { useWorkspaceDispatch } from "components/workspace/hooks/useWorkspaceDispatch";

import { useDeleteForecast } from "../../arps/hooks/useDeleteForecast";
import { useSelectedProjectPermissions } from "../projects/hooks";
import ExportForecastsButton, { WellSource } from "./ExportForecastsButton";
import { TypeWellSaveAs } from "./TypeWellSaveAs";
import { useForecastDispatch } from "./context";

interface WidgetProps {
  setWidgetActiveTabKey: () => void;
}

interface ForecastNodeActionsProps {
  fcstTwNode: UserArpsItem;
  type: ForecastActivityType;
  source: "Widget" | "Activity";
  scrollToTreeNode: (nodeKey: string) => void;
  widgetProps?: WidgetProps;
  setChecked: (existingTws: ICheckedForecast[], newTw: UserArpsItem) => void;
  onEditClick?: (event: React.MouseEvent) => void;
  onEditHandlerReady?: (
    handler: (event: React.MouseEvent, nodeToEdit?: UserArpsItem) => void
  ) => void;
}

export default function ForecastNodeActions({
  fcstTwNode,
  type,
  source,
  scrollToTreeNode,
  widgetProps,
  setChecked,
  onEditClick,
  onEditHandlerReady
}: ForecastNodeActionsProps) {
  const forecastDispatch = useForecastDispatch();
  const fsSetTemporaryForecast = useForecastStore((s) => s.setTemporaryForecast);
  const fsSetHasUnsavedChanges = useForecastStore((s) => s.setHasUnsavedChanges);
  const fsSetSelectedTypeWell = useForecastStore((s) => s.setSelectedTypeWell);
  const workspaceDispatch = useWorkspaceDispatch();
  const [isSaveAsOpen, setIsSaveAsOpen] = useState(false);
  const { settings } = useUser();
  // checkedTypeWells and checkedForecasts are now in ForecastStore (migrated from arpsSlice)
  const checkedTypeWellsStore = useForecastStore(selectCheckedTypeWells);
  const checkedForecastsStore = useForecastStore(selectCheckedForecasts);
  let checkedForecasts =
    type === TYPE_WELLS ? checkedTypeWellsStore : checkedForecastsStore;

  const [showUnsavedPopup, setShowUnsavedPopup] = useState(false);
  const temporarayForecast = useForecastStore(selectTemporaryForecast);

  const filteredForecastFolders = useSelector(
    (state: RootState) => state.arps.filteredTypeWellFolders
  );

  const hasUnsavedTypeWellChanges = useForecastStore(selectHasUnsavedChanges);

  checkedForecasts = checkedForecasts
    .map((forecast) =>
      filteredForecastFolders
        .flatMap((folder) => folder.children)
        .find((child) => child.id === forecast.id)
    )
    .filter(Boolean) as ICheckedForecast[];

  const { refetch: refetchWorkspace } = useQuery<WorkspaceQuery>(WORKSPACES, {
    context: {
      clientName: WorkspaceClientName
    },
    notifyOnNetworkStatusChange: true
  });

  const deleteForecast = useDeleteForecast(type);

  const selectedProjectPermissions = useSelectedProjectPermissions();

  function setNodeToEditable() {
    const cloneNode = { ...fcstTwNode };
    cloneNode.isEdit = true;
    cloneNode.editLocation = source;
    forecastDispatch({ payload: { forecastNameField: cloneNode.title } });

    updateTypeWellDataWithParentNode(cloneNode);
  }

  const { dashboard } = useDashboardContext();
  const hasTypeWellEditorWidget = useMemo(
    () => dashboard.widgets.find((widget) => widget.type === TypeWellEditorWidgetKey),
    [dashboard]
  );

  const { updateTypeWellDataWithParentNode } = useForecastFolderUpdater();

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  async function SwitchToTwEditorDashboard(defaultDashboard: DefaultTypeWellDashboard) {
    const { data: workspacedata } = await refetchWorkspace();
    const errorMessage =
      "Default type well dashboard does not exist. Please set a default type well dashboard in your User Settings";

    if (!settings.defaults.typeWellDashboard) {
      const defaultWorkspace = workspacedata.workspaces?.find(
        (workspace) => workspace.title === "Explore"
      );
      const dashboard = defaultWorkspace?.dashboards?.find((dashboard) => {
        return dashboard.title === "Oil TW Editor";
      });
      if (dashboard) {
        workspaceDispatch({
          payload: {
            activeDashboardId: dashboard.dashboardId,
            workspaceId: defaultWorkspace.workspaceId
          }
        });
      } else {
        toast.error(errorMessage);
      }
      return;
    }
    const dashboardExists = workspacedata.workspaces?.find((workspace) => {
      return (
        workspace.workspaceId === defaultDashboard.workspaceId &&
        workspace.dashboards?.find((dashboard) => {
          return dashboard.dashboardId === defaultDashboard.dashboardId;
        })
      );
    });
    if (dashboardExists) {
      workspaceDispatch({
        payload: {
          activeDashboardId: defaultDashboard.dashboardId,
          workspaceId: defaultDashboard.workspaceId
        }
      });
    } else {
      toast.error(errorMessage);
    }
  }

  const handleEditClick = useCallback(
    async (event: React.MouseEvent, nodeToEdit?: UserArpsItem) => {
      const targetNode = nodeToEdit || fcstTwNode;
      event.stopPropagation();

      if (widgetProps) {
        widgetProps.setWidgetActiveTabKey();
      }
      if (!hasTypeWellEditorWidget) {
        await SwitchToTwEditorDashboard(settings.defaults.typeWellDashboard);
      }

      setChecked(checkedForecasts, targetNode);

      if (onEditClick) {
        onEditClick(event);
      }
    },
    [
      widgetProps,
      hasTypeWellEditorWidget,
      settings.defaults.typeWellDashboard,
      checkedForecasts,
      fcstTwNode,
      setChecked,
      scrollToTreeNode,
      onEditClick,
      type
    ]
  );

  const handlePopupConfirmClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (hasUnsavedTypeWellChanges) {
      setShowUnsavedPopup(true);
    } else {
      handleEditClick(e);
    }
  };
  // Provide the handleEditClick function to the parent component
  useEffect(() => {
    if (onEditHandlerReady) {
      onEditHandlerReady(handleEditClick);
    }
  }, [onEditHandlerReady, handleEditClick]);

  const handleConfirmUnsavedPopup = useCallback(
    (event: React.MouseEvent) => {
      setShowUnsavedPopup(false);
      handleEditClick(event);
    },
    [handleEditClick]
  );

  const handleCancelUnsavedPopup = useCallback(() => {
    setShowUnsavedPopup(false);
  }, []);

  const TypeWellFolderMoreOptions = () => {
    return (
      <ActionListWrapper>
        {type === TYPE_WELLS && (
          <TypeWellAction
            disabled={!selectedProjectPermissions.canEditTypeWells}
            onClick={(evt) => {
              if (!selectedProjectPermissions.canEditTypeWells) return;

              // Close the popover first so Ant Design's focus-return
              // doesn't immediately blur the rename input
              setIsPopoverOpen(false);
              setNodeToEditable();
              evt.stopPropagation();
              evt.preventDefault();
            }}>
            <Icon path={mdiFormTextbox} size={"18px"} />
            Rename
          </TypeWellAction>
        )}
        {type === TYPE_WELLS && selectedProjectPermissions.canEditTypeWells && (
          <Popover
            placement="right"
            trigger="click"
            open={isSaveAsOpen}
            onOpenChange={(isOpen) => {
              setIsSaveAsOpen(isOpen);
            }}
            content={
              <TypeWellSaveAs
                isOpen={isSaveAsOpen}
                selectedTypeWellTitle={fcstTwNode.title}
                onCancel={() => setIsSaveAsOpen(false)}
              />
            }>
            <TypeWellAction
              onClick={() => {
                fsSetSelectedTypeWell({ ...fcstTwNode, editLocation: source });
              }}>
              <Icon path={mdiContentSavePlus} size={"18px"} />
              Save As
            </TypeWellAction>
          </Popover>
        )}
        <Popconfirm
          disabled={!selectedProjectPermissions.canEditTypeWells}
          placement="bottom"
          onConfirm={(evt) => {
            deleteForecast
              .delete([
                {
                  folderId: fcstTwNode.folderId,
                  id: fcstTwNode.id,
                  type: fcstTwNode.type ?? "forecast"
                }
              ])
              .then(async () => {
                forecastDispatch({ payload: { error: null } });
                return;
              })
              .catch((err) => {
                forecastDispatch({
                  payload: { error: `Error deleting. ${err.message}` }
                });
              });
            if (temporarayForecast) fsSetTemporaryForecast(null);
            fsSetHasUnsavedChanges(false);
            evt.stopPropagation();
            evt.preventDefault();
          }}
          onCancel={(evt) => {
            evt.stopPropagation();
            evt.preventDefault();
          }}
          okText="Delete"
          okType="danger"
          title={`Are you sure you want to delete the ${
            type === TYPE_WELLS ? "type well" : "forecast"
          }?`}>
          <TypeWellAction
            disabled={!selectedProjectPermissions.canEditTypeWells}
            onClick={(evt) => {
              evt.stopPropagation();
              evt.preventDefault();
            }}
            danger={true}>
            <Icon path={mdiDelete} size={"18px"} />
            Delete
          </TypeWellAction>
        </Popconfirm>
        {type === TYPE_WELLS && (
          <ExportForecastsButton
            wellSource={WellSource.TypeWells}
            node={fcstTwNode}
            type="forecast"
          />
        )}

        {type === USER_ARPS && (
          <ExportForecastsButton
            wellSource={WellSource.UserForecasts}
            node={fcstTwNode}
            type="forecast"
          />
        )}
      </ActionListWrapper>
    );
  };

  return (
    <ActionsWrapper id="actions_wrapper">
      {!fcstTwNode.source && type == "TYPEWELLS" && (
        <UnsavedTypeWellChangesPopconfirm
          open={showUnsavedPopup}
          placement="right"
          onConfirm={handleConfirmUnsavedPopup}
          onCancel={handleCancelUnsavedPopup}
          isReset={false}
          title="Your type well changes will show on charts, but won’t be permanent unless you save.">
          <ForecastActionButton
            className={"visible-on-hover"}
            onClick={handlePopupConfirmClick}>
            <Icon path={mdiPencil} size={"21px"} />
          </ForecastActionButton>
        </UnsavedTypeWellChangesPopconfirm>
      )}
      <Tooltip className={"visible-on-hover"} title="More">
        <Popover
          arrowPointAtCenter={true}
          onOpenChange={(isOpen) => {
            setIsPopoverOpen(isOpen);
          }}
          content={TypeWellFolderMoreOptions()}
          open={isPopoverOpen}
          overlayClassName="popover-no-padding"
          placement="bottomLeft"
          trigger="click">
          <ForecastActionButton
            data-testid="forecast-more-action-button"
            type={"text"}
            onClick={(event) => event.stopPropagation()}>
            <Icon path={mdiDotsVertical} size={"21px"} />
          </ForecastActionButton>
        </Popover>
      </Tooltip>
    </ActionsWrapper>
  );
}

const ActionsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;

  .title-reservecategory {
    display: block;
    background-color: #dfdfdf;
    padding: 1px 3px;
    margin-right: 5px;
    border-radius: 8px;
    font-size: 10px;
    font-weight: bold;
  }
`;

export const Actions = styled.div`
  display: flex;
  flex-direction: row;
`;

const ActionListWrapper = styled.div`
  padding: 6px;
`;

const ForecastActionButton = styled.button`
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  padding: 0;
  margin: 3px;
  cursor: pointer;
  background: transparent;
  color: ${(props) => props.color?.toString() ?? "#a2aaad"};

  &:hover {
    color: ${(props) => (props.danger ? "var(--color-danger)" : "var(--color-primary)")};
  }

  &[disabled]:hover > svg {
    cursor: default;
    color: lightgray;
  }
`;

const TypeWellAction = styled.div`
  height: 3.2rem;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--color-text-50);
  font-weight: 500;
  padding: 0 var(--space-3);
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};

  &:hover {
    color: ${(props) =>
      props.disabled
        ? "var(--color-text-50)"
        : props.danger
        ? "var(--color-danger)"
        : "var(--color-primary)"};
  }
`;
